import React from 'react';
import { FileText, Trash2, Clock, Building2, Users2, RadiationIcon as InformationIcon } from 'lucide-react';
import { ReportHistoryItem } from '../types/history';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ReportHistoryProps {
  items: ReportHistoryItem[];
  onItemClick: (item: ReportHistoryItem) => void;
  onItemRemove: (id: string) => void;
}

export const ReportHistory: React.FC<ReportHistoryProps> = ({
  items,
  onItemClick,
  onItemRemove,
}) => {
  const getOperationName = (operation: any): string | null => {
    if (!operation) return null;

    // Se for um objeto com options (caso de campo customizado do ClickUp)
    if (operation.type_config?.options) {
      const option = operation.type_config.options.find((opt: any) => opt.id === operation.value);
      return option?.name || null;
    }

    return null;
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-500 bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <InformationIcon className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-600" />
          <p>
            O histórico é armazenado localmente no seu navegador (localStorage) e mantém os últimos 20 registros.
            Ao limpar os dados do navegador, o histórico será perdido.
          </p>
        </div>
      </div>

      {!items.length ? (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p>Nenhum relatório gerado ainda</p>
          <p className="text-sm">Os últimos 20 relatórios gerados aparecerão aqui</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-lg shadow-sm border border-gray-100 hover:border-blue-200 transition-colors"
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <button
                    onClick={() => onItemClick(item)}
                    className="flex-1 text-left"
                  >
                    <h3 className="font-medium text-gray-900 mb-2 line-clamp-2">
                      {item.taskName}
                    </h3>
                    <div className="space-y-1">
                      {item.operation && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Building2 className="w-4 h-4" />
                          <span>{getOperationName(item.operation)}</span>
                        </div>
                      )}
                      {item.team && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Users2 className="w-4 h-4" />
                          <span>{item.team}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Clock className="w-4 h-4" />
                        <span>
                          {format(new Date(item.date), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", {
                            locale: ptBR,
                          })}
                        </span>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => onItemRemove(item.id)}
                    className="text-gray-400 hover:text-red-600 transition-colors p-1"
                    title="Remover do histórico"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};